import { memo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import { trackEvent } from '../../utils/analytics'
import debugWrapper from '../../utils/debugWrapper'

/**
 * ImmersivePanelHeader - Header for the left-side immersive panel
 *
 * Layout: [Logo] + [Collapse ChevronLeft] on first row, tabs below.
 *
 * @idempotent - Pure component, same props = same output
 * @atomic - Single responsibility: render panel header
 */
const ImmersivePanelHeader = memo(function ImmersivePanelHeader({
  immersiveTabs,
  user,
  onLogout,
  isMinimized = false,
  isLoading = false,
  onCollapse = null,
  entityName = null,
  entityCategory = null,
}) {
  const location = useLocation()

  const handleLogoClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault()
      window.location.href = '/'
    }
    trackEvent('Navigation', 'logo_click', 'control_panel_header', null, {
      user_authenticated: !!user,
      user_id: user?.id || null,
      is_minimized: isMinimized,
      device_type: window.innerWidth < 640 ? 'mobile' : 'desktop'
    })
  }

  return (
    <div className="py-1.5 px-3">

      {/* Row 1: Logo centered, collapse button pinned to right */}
      <div className="relative flex items-center justify-center mb-1">
        <Link
          to="/"
          className="flex items-center justify-center"
          onClick={handleLogoClick}
        >
          <img
            src="/logo.png"
            alt="TrckIt Logo"
            className={`w-auto h-[50px] transition-transform duration-700 ${isLoading ? 'animate-spin' : ''}`}
          />
        </Link>

        {onCollapse && (
          <button
            className="absolute right-0 flex items-center gap-1 px-2 py-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors text-xs font-medium"
            aria-label="Collapse panel"
            onClick={onCollapse}
            onTouchEnd={(e) => { e.preventDefault(); onCollapse() }}
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Entity name strip — only shown on entity detail map when panel is expanded */}
      {entityName && !isMinimized && (
        <div className="flex items-center gap-2 px-3 py-1.5 mb-1.5 bg-primary/10 rounded-lg">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{entityName}</p>
            {entityCategory && (
              <span className="inline-block text-[10px] text-primary/70 capitalize">
                {entityCategory.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Row 2: Tab navigation */}
      <div className="flex items-center w-full py-1">
        {immersiveTabs}
      </div>
    </div>
  )
})

export default debugWrapper(ImmersivePanelHeader, 'ImmersivePanelHeader', 'src/components/map/ImmersivePanelHeader.jsx')
